/**
 * ResourceService — CRUD over
 *   projects/{pid}/topics/{topicId}/resources/{resourceId}
 *
 * Resource (architecture/новые сущности.md) is a knowledge unit attached to
 * a Topic — stat / quote / case_study / example / link / image / note. The
 * MasterPostHandler reads `resources[]` at handler start and prepends them
 * to the AI input so the platform-agnostic master post can cite real
 * evidence. Derivative posts inherit the citations via the master.
 *
 * Firestore-direct mirror of TopicPillarService — all writes go through
 * stripUndefined and use addDoc/setDoc/updateDoc/deleteDoc. No security
 * rules added in this iteration; rules inherit project scope from the
 * outer doc.
 */

import {
  collection,
  doc,
  addDoc,
  updateDoc,
  deleteDoc,
  serverTimestamp,
} from 'firebase/firestore';
import { CoreFirebaseService } from '@modules/firebase-core/services/CoreFirebaseService';
import { stripUndefined } from '../utils/stripUndefined';
import type { Resource } from '../types';

export type ResourceCreate = Omit<Resource, 'id' | 'createdAt' | 'updatedAt'>;
export type ResourcePatch = Partial<Omit<Resource, 'id' | 'topicId' | 'createdAt'>>;

export class ResourceService {
  private get db() {
    return CoreFirebaseService.getInstance().firestore;
  }

  private collectionRef(projectId: string, topicId: string) {
    return collection(
      this.db,
      'projects',
      projectId,
      'topics',
      topicId,
      'resources',
    );
  }

  private docRef(projectId: string, topicId: string, resourceId: string) {
    return doc(
      this.db,
      'projects',
      projectId,
      'topics',
      topicId,
      'resources',
      resourceId,
    );
  }

  async create(
    projectId: string,
    topicId: string,
    resource: ResourceCreate,
  ): Promise<string> {
    const ref = this.collectionRef(projectId, topicId);
    const stripped = stripUndefined({
      ...resource,
      topicId,
      createdAt: serverTimestamp() as any,
      updatedAt: serverTimestamp() as any,
    }) as Record<string, unknown>;
    const created = await addDoc(ref, stripped);
    return created.id;
  }

  async update(
    projectId: string,
    topicId: string,
    resourceId: string,
    patch: ResourcePatch,
  ): Promise<void> {
    const stripped = stripUndefined({
      ...patch,
      updatedAt: serverTimestamp() as any,
    }) as Record<string, unknown>;
    await updateDoc(this.docRef(projectId, topicId, resourceId), stripped);
  }

  async delete(
    projectId: string,
    topicId: string,
    resourceId: string,
  ): Promise<void> {
    await deleteDoc(this.docRef(projectId, topicId, resourceId));
  }
}

export const resourceService = new ResourceService();
